import { useAtomValue } from "jotai";
import { useCurrentUser } from "src/business-hooks/useCurrentUser";
import { refreshTokenClaimsAtom } from "src/utils/auth/auth-atoms";
import type { TokenResponse } from "src/utils/auth/auth-schema";
import { authService } from "src/utils/auth/auth-service";
import { dfetch } from "src/utils/dfetch.ts";
import { notify } from "@utils/notification.ts";
import {
  type ImpersonationUser,
  selectTenantUser,
} from "./SuperAdmin.impersonation";
import type { TenantResponse, TenantWithGrouping } from "./SuperAdmin.types";

export interface DecodedLicense {
  pipelines: number;
  vcoreHours: number;
  insights: boolean;
  recommendations: boolean;
  exp: number;
}

export const decodeLicenseJwt = (jwt: string | null): DecodedLicense | null => {
  if (!jwt) return null;
  try {
    const payload = jwt.split(".")[1];
    const decoded = JSON.parse(atob(payload));
    return {
      pipelines: decoded.count_allowed_pipelines,
      vcoreHours: decoded.vcore_hours_limit,
      insights: decoded.is_insights_allowed,
      recommendations: decoded.is_recommendations_allowed ?? false,
      exp: decoded.exp,
    };
  } catch {
    return null;
  }
};

export const calculateDaysRemaining = (exp: number): number => {
  const now = Date.now() / 1000;
  return Math.ceil((exp - now) / (60 * 60 * 24));
};

export const transformTenantsForGrid = (
  data: TenantResponse[]
): TenantWithGrouping[] => {
  const existingTenantsSet = new Set<number>();

  return data
    .sort((a, b) => Number(b.is_tenant_admin) - Number(a.is_tenant_admin))
    .map((d: TenantResponse) => {
      const dd = d as TenantWithGrouping;
      dd.grouping = [d.tenant_id + ""];

      if (!existingTenantsSet.has(d.tenant_id)) {
        existingTenantsSet.add(d.tenant_id);
        return dd;
      }

      dd.grouping.push(d.user_id + "");

      return dd;
    });
};

export const impersonateUser = async (user_id: number) => {
  const token = (await dfetch(`/api/super_admin/users/${user_id}/token`).then((j) =>
    j.json()
  )) as TokenResponse;

  authService.storeSession({
    access_token: token.access_token,
    refresh_token: token.refresh_token,
    message: null,
  });

  notify("Impersonated user", {
    variant: "success",
  });
};

export const useIsImpersonated = () => {
  const currentUser = useCurrentUser();
  const refreshToken = useAtomValue(refreshTokenClaimsAtom);
  const isImpersonated =
    refreshToken?.user_id &&
    currentUser?.user_id &&
    refreshToken.user_id !== currentUser.user_id;

  return isImpersonated;
};

export const impersonateTenant = async (tenant_id: number, userId?: number) => {
  const users = (await dfetch("/api/super_admin/users").then((j) =>
    j.json()
  )) as ImpersonationUser[];

  const user = selectTenantUser(users, tenant_id, userId);
  if (!user) {
    throw new Error(
      userId
        ? `User ${userId} not found in tenant ${tenant_id}`
        : "No users found for this tenant"
    );
  }

  await impersonateUser(user.user_id);
};
